import React, { useEffect, useRef, useState } from "react";
import {
  Upload, X, Image as ImageIcon, Save, ArrowLeft, Trash2, Instagram,
} from "lucide-react";
import { useNavigate, useParams } from "react-router-dom";
import { api, uploadImage } from "../api";

const emptyForm = {
  name: "",
  title: "",
  bio: "",
  specialties: [],
  stats: [
    { label: "Years Active", value: "" },
    { label: "Productions", value: "" },
    { label: "Countries", value: "" },
    { label: "Award Wins", value: "" },
  ],
  image: "",
  galleryImages: [],
  videoUrl: "",
  instagramUrl: "",
  instagramPosts: [],
  featured: false,
};

const AdminArtistForm = ({ mode }) => {
  const { id } = useParams();
  const navigate = useNavigate();
  const [form, setForm] = useState(emptyForm);
  const [loading, setLoading] = useState(mode === "edit");
  const [saving, setSaving] = useState(false);
  const [saved, setSaved] = useState(false);
  const [error, setError] = useState("");
  const [specialtyInput, setSpecialtyInput] = useState("");
  const [dragOver, setDragOver] = useState(false);
  const [uploadingMain, setUploadingMain] = useState(false);
  const [uploadingGallery, setUploadingGallery] = useState(false);
  const mainInputRef = useRef(null);
  const galleryInputRef = useRef(null);

  useEffect(() => {
    window.scrollTo(0, 0);
    if (mode === "edit" && id) {
      api
        .get(`/artists/${id}`)
        .then((r) => {
          const a = r.data;
          setForm({
            name: a.name || "",
            title: a.title || "",
            bio: a.bio || "",
            specialties: a.specialties || [],
            stats: a.stats?.length ? a.stats : emptyForm.stats,
            image: a.image || "",
            galleryImages: a.galleryImages || [],
            videoUrl: a.videoUrl || "",
            instagramUrl: a.instagramUrl || "",
            instagramPosts: a.instagramPosts || [],
            featured: a.featured || false,
          });
        })
        .catch(() => setError("Failed to load artist"))
        .finally(() => setLoading(false));
    }
  }, [mode, id]);

  const addSpecialty = () => {
    const v = specialtyInput.trim();
    if (v && !form.specialties.includes(v)) {
      setForm({ ...form, specialties: [...form.specialties, v] });
      setSpecialtyInput("");
    }
  };

  const removeSpecialty = (spec) => {
    setForm({ ...form, specialties: form.specialties.filter((s) => s !== spec) });
  };

  const handleMainUpload = async (file) => {
    if (!file) return;
    setUploadingMain(true);
    setError("");
    try {
      const url = await uploadImage(file);
      setForm((f) => ({ ...f, image: url }));
    } catch (err) {
      setError(err?.response?.data?.detail || "Upload failed");
    } finally {
      setUploadingMain(false);
    }
  };

  const handleGalleryUpload = async (files) => {
    if (!files || !files.length) return;
    setUploadingGallery(true);
    setError("");
    try {
      const urls = await Promise.all([...files].map((f) => uploadImage(f)));
      setForm((f) => ({ ...f, galleryImages: [...f.galleryImages, ...urls] }));
    } catch (err) {
      setError(err?.response?.data?.detail || "Upload failed");
    } finally {
      setUploadingGallery(false);
    }
  };

  const removeGalleryImage = (idx) => {
    setForm((f) => ({ ...f, galleryImages: f.galleryImages.filter((_, i) => i !== idx) }));
  };

  const handleSave = async () => {
    setError("");
    setSaving(true);
    try {
      const payload = {
        ...form,
        image: form.image || "/images/artist-1.jpg",
        galleryImages: form.galleryImages.length ? form.galleryImages : [form.image || "/images/artist-1.jpg"],
      };
      if (mode === "add") await api.post("/artists", payload);
      else await api.put(`/artists/${id}`, payload);
      setSaved(true);
      setTimeout(() => navigate("/admin"), 900);
    } catch (err) {
      setError(err?.response?.data?.detail || "Save failed");
    } finally {
      setSaving(false);
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-[#121212]">
        <div className="w-10 h-10 border-2 border-gold/20 border-t-gold rounded-full animate-spin" />
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-[#121212] pt-24 pb-20" data-testid="admin-artist-form">
      <div className="max-w-4xl mx-auto px-6">
        <div className="flex items-center gap-4 mb-10">
          <button
            onClick={() => navigate("/admin")}
            className="flex items-center gap-2 text-white/40 hover:text-gold transition-colors text-xs font-body tracking-wider uppercase"
          >
            <ArrowLeft size={13} />
            <span>Back</span>
          </button>
          <div className="h-px flex-1 bg-gold/10" />
        </div>

        <div className="flex items-center gap-3 mb-3">
          <div className="h-px w-8 bg-gold/60" />
          <span className="font-body text-gold text-[0.65rem] font-semibold tracking-[0.4em] uppercase">
            {mode === "add" ? "New Artist" : "Edit Artist"}
          </span>
        </div>
        <h1 className="font-display font-light text-5xl text-white mb-10">
          {mode === "add" ? "Add to Roster" : `Editing: ${form.name}`}
        </h1>

        <div className="space-y-8">
          {/* Basic Info */}
          <div className="glass rounded-sm p-8">
            <h3 className="font-heading text-sm font-semibold tracking-widest text-white/60 uppercase mb-6">Basic Information</h3>
            <div className="grid md:grid-cols-2 gap-5">
              <div>
                <label className="font-body text-white/40 text-[0.65rem] tracking-[0.2em] uppercase block mb-2">Full Name *</label>
                <input
                  value={form.name}
                  onChange={(e) => setForm({ ...form, name: e.target.value })}
                  placeholder="Artist full name"
                  className="input-gold w-full px-4 py-3 rounded-sm text-sm font-body"
                  data-testid="form-name"
                />
              </div>
              <div>
                <label className="font-body text-white/40 text-[0.65rem] tracking-[0.2em] uppercase block mb-2">Title / Role *</label>
                <input
                  value={form.title}
                  onChange={(e) => setForm({ ...form, title: e.target.value })}
                  placeholder="e.g. Urban & Contemporary Lead"
                  className="input-gold w-full px-4 py-3 rounded-sm text-sm font-body"
                  data-testid="form-title"
                />
              </div>
            </div>
            <div className="mt-5">
              <label className="font-body text-white/40 text-[0.65rem] tracking-[0.2em] uppercase block mb-2">Biography *</label>
              <textarea
                value={form.bio}
                onChange={(e) => setForm({ ...form, bio: e.target.value })}
                placeholder="Artist biography..."
                rows={5}
                className="input-gold w-full px-4 py-3 rounded-sm text-sm font-body resize-none"
                data-testid="form-bio"
              />
            </div>
            <div className="mt-5 flex items-center gap-3">
              <input
                type="checkbox"
                id="featured"
                checked={form.featured}
                onChange={(e) => setForm({ ...form, featured: e.target.checked })}
                className="accent-gold w-4 h-4 cursor-pointer"
                data-testid="form-featured"
              />
              <label htmlFor="featured" className="font-body text-white/50 text-sm cursor-pointer">
                Featured on homepage
              </label>
            </div>
          </div>

          {/* Specialties */}
          <div className="glass rounded-sm p-8">
            <h3 className="font-heading text-sm font-semibold tracking-widest text-white/60 uppercase mb-6">Specialties</h3>
            <div className="flex gap-3 mb-4">
              <input
                value={specialtyInput}
                onChange={(e) => setSpecialtyInput(e.target.value)}
                onKeyDown={(e) => {
                  if (e.key === "Enter") {
                    e.preventDefault();
                    addSpecialty();
                  }
                }}
                placeholder="Add specialty (press Enter)"
                className="input-gold flex-1 px-4 py-3 rounded-sm text-sm font-body"
                data-testid="form-specialty-input"
              />
              <button onClick={addSpecialty} className="btn-gold px-5 py-2 text-xs rounded-sm" data-testid="form-specialty-add">
                Add
              </button>
            </div>
            <div className="flex flex-wrap gap-2">
              {form.specialties.map((spec) => (
                <span key={spec} className="specialty-tag flex items-center gap-2 cursor-default">
                  {spec}
                  <button onClick={() => removeSpecialty(spec)} className="hover:text-white">
                    <X size={10} />
                  </button>
                </span>
              ))}
            </div>
          </div>

          {/* Stats */}
          <div className="glass rounded-sm p-8">
            <h3 className="font-heading text-sm font-semibold tracking-widest text-white/60 uppercase mb-6">Career Statistics</h3>
            <div className="grid sm:grid-cols-2 gap-4">
              {form.stats.map((stat, i) => (
                <div key={stat.label}>
                  <label className="font-body text-white/40 text-[0.65rem] tracking-[0.2em] uppercase block mb-2">{stat.label}</label>
                  <input
                    value={stat.value}
                    onChange={(e) => {
                      const ns = [...form.stats];
                      ns[i] = { ...stat, value: e.target.value };
                      setForm({ ...form, stats: ns });
                    }}
                    placeholder={stat.label === "Years Active" ? "e.g. 10+" : "e.g. 50"}
                    className="input-gold w-full px-4 py-3 rounded-sm text-sm font-body"
                  />
                </div>
              ))}
            </div>
          </div>

          {/* Media */}
          <div className="glass rounded-sm p-8">
            <h3 className="font-heading text-sm font-semibold tracking-widest text-white/60 uppercase mb-6">Media</h3>

            {/* Main profile image */}
            <label className="font-body text-white/40 text-[0.65rem] tracking-[0.2em] uppercase block mb-3">
              <ImageIcon size={12} className="text-gold/50 inline mr-2" />
              Profile Image
            </label>
            <div className="flex gap-4 items-start mb-6">
              {form.image ? (
                <div className="relative w-32 h-32 rounded-sm overflow-hidden border border-gold/30 flex-shrink-0">
                  <img src={form.image} alt="Profile" className="w-full h-full object-cover" />
                </div>
              ) : null}
              <div className="flex-1 space-y-2">
                <button
                  onClick={() => mainInputRef.current?.click()}
                  disabled={uploadingMain}
                  className="btn-outline-gold px-4 py-2.5 text-xs rounded-sm flex items-center gap-2"
                  data-testid="form-upload-main"
                >
                  {uploadingMain ? (
                    <><div className="w-3 h-3 border-2 border-gold/30 border-t-gold rounded-full animate-spin" /><span>Uploading...</span></>
                  ) : (
                    <><Upload size={12} /><span>Upload Profile Image</span></>
                  )}
                </button>
                <input
                  ref={mainInputRef}
                  type="file"
                  accept="image/*"
                  className="hidden"
                  onChange={(e) => handleMainUpload(e.target.files?.[0])}
                  data-testid="form-file-main"
                />
                <div className="flex items-center gap-2">
                  <span className="font-body text-white/30 text-[0.6rem] tracking-wider uppercase">or URL:</span>
                  <input
                    value={form.image}
                    onChange={(e) => setForm({ ...form, image: e.target.value })}
                    placeholder="/images/artist.jpg"
                    className="input-gold flex-1 px-3 py-2 rounded-sm text-xs font-body"
                  />
                </div>
              </div>
            </div>

            {/* Gallery drop zone */}
            <label className="font-body text-white/40 text-[0.65rem] tracking-[0.2em] uppercase block mb-3">
              <ImageIcon size={12} className="text-gold/50 inline mr-2" />
              Gallery Images
            </label>
            <div
              onDragOver={(e) => { e.preventDefault(); setDragOver(true); }}
              onDragLeave={() => setDragOver(false)}
              onDrop={(e) => {
                e.preventDefault();
                setDragOver(false);
                handleGalleryUpload(e.dataTransfer.files);
              }}
              onClick={() => galleryInputRef.current?.click()}
              className={`drop-zone rounded-sm p-10 text-center cursor-pointer mb-5 ${dragOver ? "active" : ""}`}
              data-testid="form-gallery-dropzone"
            >
              <input
                ref={galleryInputRef}
                type="file"
                accept="image/*"
                multiple
                className="hidden"
                onChange={(e) => handleGalleryUpload(e.target.files)}
              />
              <Upload size={32} className="text-gold/40 mx-auto mb-4" />
              <p className="font-body text-white/40 text-sm mb-1">
                {uploadingGallery ? "Uploading..." : "Drag & drop images here, or click to select"}
              </p>
              <p className="font-body text-white/20 text-xs">JPG, PNG, WEBP, GIF — Max 8MB per file</p>
            </div>

            {form.galleryImages.length > 0 && (
              <div className="grid grid-cols-3 md:grid-cols-4 gap-3 mb-6">
                {form.galleryImages.map((img, idx) => (
                  <div key={idx} className="relative group rounded-sm overflow-hidden border border-gold/20" style={{ paddingBottom: "100%" }}>
                    <img src={img} alt={`Gallery ${idx}`} className="absolute inset-0 w-full h-full object-cover" />
                    <button
                      onClick={() => removeGalleryImage(idx)}
                      className="absolute top-1 right-1 w-7 h-7 rounded-sm bg-black/70 border border-red-500/50 text-red-400 flex items-center justify-center opacity-0 group-hover:opacity-100 transition-opacity hover:bg-red-500/20"
                    >
                      <Trash2 size={11} />
                    </button>
                  </div>
                ))}
              </div>
            )}

            {/* Instagram */}
            <div className="grid md:grid-cols-1 gap-5">
              <div>
                <label className="font-body text-white/40 text-[0.65rem] tracking-[0.2em] uppercase block mb-2 flex items-center gap-2">
                  <Instagram size={12} className="text-gold/50" />
                  Instagram Profile URL or @handle
                </label>
                <input
                  value={form.instagramUrl}
                  onChange={(e) => setForm({ ...form, instagramUrl: e.target.value })}
                  placeholder="https://www.instagram.com/jordan_khalil/  —or—  jordan_khalil"
                  className="input-gold w-full px-4 py-3 rounded-sm text-sm font-body"
                  data-testid="form-instagram-url"
                />
              </div>
              <div>
                <label className="font-body text-white/40 text-[0.65rem] tracking-[0.2em] uppercase block mb-2 flex items-center gap-2">
                  <Instagram size={12} className="text-gold/50" />
                  Instagram Posts to Feature (one URL per line — supports posts &amp; reels)
                </label>
                <textarea
                  value={(form.instagramPosts || []).join("\n")}
                  onChange={(e) =>
                    setForm({
                      ...form,
                      instagramPosts: e.target.value
                        .split("\n")
                        .map((s) => s.trim())
                        .filter(Boolean),
                    })
                  }
                  rows={5}
                  placeholder={"https://www.instagram.com/p/Cabc123XYZ/\nhttps://www.instagram.com/reel/Cdef456ABC/"}
                  className="input-gold w-full px-4 py-3 rounded-sm text-sm font-body resize-none"
                  data-testid="form-instagram-posts"
                />
                <p className="font-body text-white/30 text-[0.6rem] tracking-wider mt-2">
                  Tip: posts will appear as a feed grid on the artist profile. If left blank, only a “Follow on Instagram” card is shown.
                </p>
              </div>
            </div>
          </div>

          {error && (
            <div className="bg-red-500/10 border border-red-500/30 rounded-sm px-4 py-3">
              <p className="font-body text-red-400 text-xs">{error}</p>
            </div>
          )}

          <div className="flex items-center gap-4">
            <button
              onClick={handleSave}
              disabled={!form.name || !form.title || !form.bio || saving || saved}
              className={`btn-gold px-10 py-4 text-xs rounded-sm flex items-center gap-3 ${(!form.name || !form.title || !form.bio) ? "opacity-50 cursor-not-allowed" : ""}`}
              data-testid="form-save"
            >
              {saving ? (
                <><div className="w-3 h-3 border-2 border-black/30 border-t-black rounded-full animate-spin" /><span>Saving...</span></>
              ) : saved ? (
                <span>✓ Saved Successfully</span>
              ) : (
                <><Save size={14} /><span>{mode === "add" ? "Add to Roster" : "Save Changes"}</span></>
              )}
            </button>
            <button onClick={() => navigate("/admin")} className="btn-outline-gold px-8 py-4 text-xs rounded-sm">
              Cancel
            </button>
          </div>
        </div>
      </div>
    </div>
  );
};

export default AdminArtistForm;
